import React, { useCallback, useMemo } from "react";
import * as _ from "lodash";
import * as NP from "number-precision";
import DeliveryPeriod, { Bid } from "domain/deliveryPeriod";
import { ColDef } from "@ag-grid-community/all-modules";
import { AgGridReact } from "@ag-grid-community/react";
import defaultGridOptions from "common/agGrid/defaultGridOptions";
import FuturesContract from "domain/futuresContract";
import { roundCents } from "common/decimal";
import { RoundingStrategyEnum } from "domain/roundingStrategy";
import FuturesContractStreamingData from "features/futuresContracts/futuresContractPrice";
import usePriceUpdates from "features/futuresContracts/usePriceUpdates";
import FuturesContractPrice from "features/futuresContracts/futuresContractPrice";

export type TableProps = {
    deliveryPeriods: DeliveryPeriod[];
    columnDefinitions: ColDef[];
}

const defaultColDef: ColDef = {suppressMenu: true};
export const BidTable: React.FC<TableProps> = React.memo(props => {

    const withPrices = usePriceUpdates(props.deliveryPeriods, m => m.contract?.symbol, updatePrice)
    const getDeliveryPeriodId = useCallback((params: any) => params.data.id, []);

    const bidsContext = useMemo(() => {
        const uniqueContracts = _.uniqBy(withPrices.filter(m => m.contract != null).map(m => m.contract), m => m.id);
        return {
            futuresContracts: uniqueContracts, 
            lastUpdatedDate: _.max(uniqueContracts.map(x => x.priceUpdated))
        };
    }, [withPrices])
    
    function updatePrice(dp: DeliveryPeriod, price: FuturesContractPrice) {
        return { ...dp, 
            contract: { ...dp.contract, ...price },
            bids: dp.bids
            .filter(x=>x.basis != null)
            .map(b => updateBid(b, price, dp.commodity.roundingOnClose, dp.commodity.roundingInSession))
        };
    }

    function updateBid(bid: Bid, priceUpdate: FuturesContractStreamingData, roundingOnClose: RoundingStrategyEnum, roundingInSession: RoundingStrategyEnum): Bid {
        const newCashPrice = roundCents(NP.plus(priceUpdate.price, bid.basis ?? 0), (priceUpdate.isSettled ? roundingOnClose : roundingInSession));
        //Don't trigger a re-render if the price hasn't changed
        return newCashPrice === bid.cash
            ? bid
            : { ...bid, cash: newCashPrice}
    }

    return <div className="ag-theme-alpine bid-table" style={{ width: "100%" }}>
        <BidsContext.Provider value={bidsContext}>
            <AgGridReact
                {...defaultGridOptions}
                columnDefs={props.columnDefinitions}
                rowData={withPrices}
                getRowId={getDeliveryPeriodId}
                defaultColDef={defaultColDef}
                domLayout="autoHeight"
                />
        </BidsContext.Provider>
    </div>
});

export interface IBidsContext {
    futuresContracts: FuturesContract[];
    lastUpdatedDate: Date;
}

export const BidsContext = React.createContext<IBidsContext>({
    futuresContracts: [],
    lastUpdatedDate: null
});